import { Reveal } from "@components/ui";
import portalImage from "@assets/images/Frame-2147224419@2x.png";
import type { ReactNode } from "react";

interface PortalFeature {
  label: string;
  icon: ReactNode;
}

const allFeatures: PortalFeature[] = [
  {
    label: "View bills",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <path
          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    label: "Pay online",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <rect
          x="1"
          y="4"
          width="22"
          height="16"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M1 10h22" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    label: "View consumption history",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <path
          d="M18 20V10M12 20V4M6 20v-6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Submit service requests",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <path
          d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Request safety inspection",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <path
          d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Update contact details",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <path
          d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Download receipts",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-5 md:h-5">
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 2v6h6M12 18v-6M9 15l3 3 3-3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

export function CustomerPortalSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-10 md:py-14 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 lg:gap-16">
          {/* Left Content */}
          <Reveal direction="left" className="flex-1 min-w-0">
            <div className="flex flex-col gap-4 md:gap-[22px]">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase font-albert-sans w-fit">
                Portal
              </span>
              <h2 className="text-[24px] md:text-[40px] lg:text-[54px] tracking-[-0.04em] leading-[1.3] font-semibold text-[#122d63] font-albert-sans">
                Customer Portal Features
              </h2>

              <div className="flex flex-col gap-3 md:gap-4">
                <p className="text-[14px] md:text-base lg:text-[24px] tracking-[-0.04em] leading-8 font-medium text-[#62636c] font-albert-sans">
                  Your portal gives you full control over your gas services
                </p>

                {/* Feature List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                  {allFeatures.map((feature) => (
                    <div
                      key={feature.label}
                      className="flex items-center gap-2.5 md:gap-3 p-2.5 md:p-3 rounded-[12px] hover:bg-[#f8f9fb] hover:shadow-[0_2px_12px_rgba(0,0,0,0.05)] border border-transparent hover:border-[#e8e9ed] transition-all duration-300"
                    >
                      <div className="w-8 h-8 md:w-9 md:h-9 rounded-[8px] bg-[#122d63]/8 text-[#122d63] flex items-center justify-center shrink-0">
                        {feature.icon}
                      </div>
                      <span className="text-[14px] md:text-base lg:text-[20px] tracking-[-0.04em] leading-[1.4] text-[#1e1f24] font-albert-sans">
                        {feature.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right Image */}
          <Reveal direction="right" className="w-full lg:w-[571px] shrink-0">
            <img
              src={portalImage}
              alt="Customer portal interface"
              className="w-full h-auto object-cover rounded-[12px] md:rounded-[24px]"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
